import React from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import type { SerializedAssetPublish } from 'fym-shared/src/music24/oneOffPublish';
import { TypedMemo } from '../../../common/types';
import { publishMessages } from './publishMessages';

interface PublishReplaceChoiceProps {
  /** The live record of another revision of the same asset type. */
  live: SerializedAssetPublish;
  replace: boolean;
  onChange: (replace: boolean) => void;
}

/**
 * "Replace the video on YouTube?" (owner decision 2026-10-09). Replace (the default): when the new
 * revision is public, the old video turns private (not deleted) and the SmartLink plays the new one.
 * Keep both: a second video goes up.
 */
const PublishReplaceChoiceComponent: React.FC<PublishReplaceChoiceProps> = ({ live, replace, onChange }) => {
  const intl = useIntl();
  return (
    <fieldset className="d-flex flex-column gap-2" data-testid="publish-replace">
      <legend className="fs-6 fw-semibold mb-1">{intl.formatMessage(publishMessages.replaceTitle)}</legend>
      <small className="text-muted">
        {intl.formatMessage(publishMessages.replaceLive, { revision: live.revision })}{' '}
        {live.externalUrl !== null && (
          <a href={live.externalUrl} target="_blank" rel="noreferrer">
            {intl.formatMessage(publishMessages.actionOpen, { channel: 'YouTube' })}
          </a>
        )}
      </small>
      {[true, false].map((answer) => (
        <div className="form-check" key={String(answer)}>
          <Input
            id={`publish-replace-${String(answer)}`}
            type="radio"
            name="publish-replace"
            className="form-check-input"
            checked={replace === answer}
            onChange={() => onChange(answer)}
          />
          <label className="form-check-label" htmlFor={`publish-replace-${String(answer)}`}>
            {intl.formatMessage(answer ? publishMessages.replaceYes : publishMessages.replaceKeepBoth)}
          </label>
        </div>
      ))}
    </fieldset>
  );
};

export const PublishReplaceChoice = TypedMemo(PublishReplaceChoiceComponent);
